<template>
  <v-card>
    <v-card-title>All Games</v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center pa-4">Loading…</div>

      <v-list v-else lines="two">
        <div v-if="games.length === 0" class="text-center pa-4">No games found.</div>

        <v-list-item v-for="game in games" :key="game.id">
          <div style="display: flex; align-items: center; gap: 12px; width: 100%; flex-wrap: wrap;">
            <!-- Name -->
            <span style="min-width: 160px; font-weight: 500;">{{ game.name }}</span>

            <!-- Owner (editable) -->
            <v-text-field
              v-model="game.addedby"
              label="Owner"
              density="compact"
              hide-details
              style="max-width: 200px;"
              @blur="saveGame(game)"
            />

            <!-- Global toggle -->
            <v-chip
              :color="game.global ? 'green' : 'grey'"
              variant="tonal"
              style="cursor: pointer;"
              @click="toggleGlobal(game)"
            >
              {{ game.global ? 'Global' : 'Local' }}
            </v-chip>

            <!-- Delete -->
            <v-btn icon color="error" size="small" @click="requestDelete(game)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </v-list-item>
      </v-list>

      <!-- Add new row -->
      <v-divider class="my-4" />
      <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
        <v-text-field v-model="newName"  label="Name"  density="compact" hide-details style="max-width: 200px;" />
        <v-text-field v-model="newOwner" label="Owner" density="compact" hide-details style="max-width: 200px;" />
        <v-btn color="primary" @click="addGame">Add</v-btn>
      </div>
    </v-card-text>
  </v-card>

  <!-- Delete confirmation -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title>Delete Game?</v-card-title>
      <v-card-text>Are you sure you want to delete <strong>{{ toDelete?.name }}</strong>?</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
        <v-btn color="error" text @click="confirmDelete">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { GAMES_DB } from '@/constants/constants';

const games = ref([]);
const loading = ref(false);
const deleteDialog = ref(false);
const toDelete = ref(null);
const newName = ref('');
const newOwner = ref('');

async function load() {
  loading.value = true;
  const snapshot = await getDocs(collection(db, GAMES_DB));
  games.value = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  loading.value = false;
}

async function saveGame(game) {
  await updateDoc(doc(db, GAMES_DB, game.id), { addedby: game.addedby });
}

async function toggleGlobal(game) {
  game.global = !game.global;
  await updateDoc(doc(db, GAMES_DB, game.id), { global: game.global });
}

function requestDelete(game) {
  toDelete.value = game;
  deleteDialog.value = true;
}

async function confirmDelete() {
  if (!toDelete.value) return;
  await deleteDoc(doc(db, GAMES_DB, toDelete.value.id));
  games.value = games.value.filter(g => g.id !== toDelete.value.id);
  deleteDialog.value = false;
  toDelete.value = null;
}

async function addGame() {
  const name = newName.value.trim();
  const owner = newOwner.value.trim();
  if (!name) return;
  const slugify = require('slugify');
  const id = slugify(name, { replacement: '-', remove: /[*+~%#.()"!:@/]/g, lower: true });
  await setDoc(doc(db, GAMES_DB, id), { name, addedby: owner, global: false });
  games.value.push({ id, name, addedby: owner, global: false });
  newName.value = '';
  newOwner.value = '';
}

onMounted(load);
</script>
